import React, { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';

import { Table, type TableSort } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  CaseStatus,
  ComplianceCaseListItem,
  fetchComplianceCases,
  formatCaseReasonLabel,
  formatCaseUserName,
  formatOpenFor,
} from 'src/api/adminCompliance';
import { formatUserStatusLabel } from 'src/api/adminUsers';
import { convertDate, formatFieldString } from 'src/components/Table/helpers';
import { ROUTE_USERS } from 'src/utils/routes';

import CaseDetailsModal from './CaseDetailsModal';
import { StatusBadge, UserLink } from './styled';

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
];

const CASE_REASONS = [
  'PROHIBITED_WITHDRAWAL',
  'SCREENING_FAILED',
  'SANCTIONED_DEPOSIT',
  'HIGH_RISK_DEPOSIT_HOLD',
  'COMPLIANCE_HOLD_WITHDRAWAL',
];

const REASON_OPTIONS = CASE_REASONS.map((value) => ({ value, label: formatCaseReasonLabel(value) }));

// Cases are only opened at HIGH and above, so the lower levels would never match.
const RISK_OPTIONS = [
  { value: 'HIGH', label: 'High' },
  { value: 'PROHIBITED', label: 'Prohibited' },
];

// One bar per filter; the API ANDs them (`?status=&reason=&risk_level=`).
const CASE_FILTERS: FilterDef[] = [
  { key: 'status', label: 'Status', options: STATUS_OPTIONS, allLabel: 'All statuses' },
  { key: 'reason', label: 'Reason', options: REASON_OPTIONS, allLabel: 'All reasons' },
  { key: 'risk_level', label: 'Risk level', options: RISK_OPTIONS, allLabel: 'All risk levels' },
];

// Columns the API can order by, mapped from the table's field name.
const SORT_FIELDS: Record<string, string> = {
  open_for_display: 'opened_at',
  status_display: 'status',
};

const HEADERS = [
  'Case #',
  'User',
  'Account status',
  'Reason',
  'Risk',
  'Txn',
  'Status',
  'Open for',
];

type CasesTabProps = {
  onTotalChange?: Dispatch<SetStateAction<number | null>>;
};

const CasesTab: React.FC<CasesTabProps> = ({ onTotalChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState<ComplianceCaseListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selectedCaseNumber, setSelectedCaseNumber] = useState<number | null>(null);
  const [sort, setSort] = useState<TableSort>(null);

  const load = useCallback(
    async (params: { page: number; filters: Record<string, string> }) => {
      setLoading(true);
      try {
        const res = await fetchComplianceCases({
          page: params.page,
          limit: 10,
          // Only opened_at and status are index-backed on the API.
          sort: (sort ? SORT_FIELDS[sort.field] : 'opened_at') as 'opened_at' | 'status',
          order: sort?.direction ?? 'desc',
          status: (params.filters.status as CaseStatus) || undefined,
          reason: params.filters.reason || undefined,
          risk_level: (params.filters.risk_level as 'HIGH' | 'PROHIBITED') || undefined,
        });
        setCases(res.cases);
        setTotalPages(Math.max(1, res.meta.page_count || 1));
        onTotalChange?.(res.meta.total_count || 0);
      } catch (e: any) {
        dispatch(
          setAppAlert({
            message: e?.response?.data?.error ?? 'Failed to load cases',
            isSuccess: false,
          }),
        );
        setCases([]);
        onTotalChange?.(0);
      } finally {
        setLoading(false);
      }
    },
    // `sort` is part of the query: a change re-runs it from page 1 via the hook.
    [dispatch, onTotalChange, sort],
  );

  const { applied: appliedFilters, refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: load });

  // A resolved case refetches the current page with the current filters.
  const reload = useCallback(
    () => load({ page: currentPage, filters: appliedFilters }),
    [appliedFilters, currentPage, load],
  );

  const rows = useMemo(
    () =>
      cases.map((item) => ({
        ...item,
        case_number_display: String(item.case_number),
        user_name: formatCaseUserName(item.user),
        account_status: formatUserStatusLabel(item.user?.status ?? undefined),
        reason_display: formatCaseReasonLabel(item.reason, item.reason_detail),
        risk_display: item.risk_level || '—',
        txn_display: item.transaction
          ? `${item.transaction.amount ?? '—'} ${item.transaction.currency_code ?? ''}`.trim()
          : '—',
        status_display: item.status || '—',
        open_for_display: formatOpenFor(item.open_for_ms),
      })),
    [cases],
  );

  return (
    <>
      <FilterBar
        filters={CASE_FILTERS}
        values={filters}
        onChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onClear={() => setFilters({})}
        onRefresh={refreshQuery}
      />

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={HEADERS}
          items={rows}
          columns="0.7fr 1.1fr 0.9fr 1.4fr 0.8fr 1fr 0.8fr 0.7fr"
          fieldsToShow={[
            'case_number_display',
            'user_name',
            'account_status',
            'reason_display',
            'risk_display',
            'txn_display',
            'status_display',
            'open_for_display',
          ]}
          sortableFields={Object.keys(SORT_FIELDS)}
          sort={sort}
          onSortChange={setSort}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => setSelectedCaseNumber(Number(item.case_number))}
          renderCell={(item, field) => {
            if (field === 'user_name') {
              const value = String(item.user_name || '—');
              const ownerId = item.user?.id;
              if (!ownerId) return <span>{value}</span>;
              return (
                <UserLink
                  type="button"
                  title={item.user?.email ? `${value} (${item.user.email})` : value}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${ROUTE_USERS}/${ownerId}`);
                  }}
                >
                  {value}
                </UserLink>
              );
            }
            if (field === 'status_display' || field === 'risk_display' || field === 'account_status') {
              const value = String(item[field] || '—');
              if (value === '—') return <span>—</span>;
              return <StatusBadge $status={value}>{formatFieldString(value)}</StatusBadge>;
            }
            if (field === 'txn_display') {
              const type = item.transaction?.type;
              const label = String(item.txn_display || '—');
              return (
                <span title={type ? formatFieldString(String(type)) : undefined}>
                  {label}
                  {type ? ` · ${formatFieldString(String(type))}` : ''}
                </span>
              );
            }
            if (field === 'open_for_display' && item.opened_at) {
              return (
                <span title={convertDate(String(item.opened_at)).date}>
                  {String(item.open_for_display)}
                </span>
              );
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}

      {selectedCaseNumber != null ? (
        <CaseDetailsModal
          caseNumber={selectedCaseNumber}
          onClose={() => setSelectedCaseNumber(null)}
          onResolved={() => {
            setSelectedCaseNumber(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
};

export default CasesTab;
